import {useMutation, useQuery, useQueryClient} from 'react-query';
import {Edge, Node} from 'reactflow';
import {AutomationService} from "crqlar-react-common/dist/services/automation/AutomationService";
import {
    AutomationFlow,
    AutomationType
} from "crqlar-react-common/dist/models/automation/automation";

import {fromFlowEdges, fromFlowNodes} from "../utils/flowGraph";
import {serverTableQueryKey} from '../../../common/serverTable/useServerTable';

export const useAutomationFlow = (id: string | undefined) => {
    const queryClient = useQueryClient();

    const {data: flowResponse, isLoading} = useQuery({
        queryKey: ['automation', id],
        queryFn: () => AutomationService.getFlow(id!),
        enabled: !!id,
        refetchOnWindowFocus: false
    });

    const flow = flowResponse;

    const saveMutation = useMutation({
        mutationFn: async ({nodes, edges}: { nodes: Node[], edges: Edge[] }) => {
            if (!flow) throw new Error('Automation is not loaded');

            const updateData: AutomationFlow = {
                nodes: fromFlowNodes(nodes),
                connections: fromFlowEdges(edges),
                type: AutomationType.ADVANCED
            };

            return AutomationService.updateFlow(id!, updateData);
        },
        onSuccess: savedFlow => {
            queryClient.setQueryData(['automation', id], savedFlow);
        }
    });

    const renameMutation = useMutation({
        mutationFn: (name: string) => {
            if (!id || !flow) throw new Error('Automation is not loaded');
            if (!name.trim()) throw new Error('Name is required');
            return AutomationService.updateFlow(id, {name: name.trim()});
        },
        onSuccess: renamed => {
            // A metadata edit must not replace the graph or newer state in the editor cache.
            queryClient.setQueryData<AutomationFlow>(['automation', renamed.id ?? id], current => current
                ? {...current, name: renamed.name, updateDate: renamed.updateDate}
                : renamed);
            for (const tableId of ['marketing-automations', 'surveys-automations']) {
                void queryClient.invalidateQueries(serverTableQueryKey(tableId));
            }
        }
    });

    return {
        flow,
        isLoading,
        saveMutation,
        renameMutation
    };
};
